/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// Pure helpers for the Walk Me Home session lifecycle (Batch D Phase 11).
// Lives outside the React layer so the same logic is testable in Vitest and
// reusable from the Firebase scheduled delay-alert function. No firebase /
// react / capacitor imports here (travelMode is a sibling pure module).

import {
  type TravelMode,
  normalizeTravelMode,
} from "@/utils/walkMeHome/travelMode";

export type WalkSessionStatus =
  | "active"
  | "arrived"
  | "overdue"
  | "cancelled";

export interface WalkSessionWatcher {
  name: string;
  /** E.164 normalized phone, e.g. +17036062559 */
  phone: string;
  /** Where the watcher came from — used so we can re-render the right tag in UI. */
  source: "safecircle" | "oneoff";
}

export interface WalkSessionShape {
  sessionId: string;
  walkerUid: string;
  walkerName: string;
  /** E.164 walker phone for emergency callbacks. May be empty when the user
   *  has not added a phone to their profile yet. */
  walkerPhone: string;
  destinationLat: number;
  destinationLng: number;
  destinationName: string;
  /** Travel mode — "walk" (default) or "drive". Absent on legacy sessions →
   *  read via normalizeTravelMode as "walk". */
  travelMode?: TravelMode;
  /** Initial origin coordinates captured at start; used by the watcher map to
   *  draw the start pin even after the walker has moved. Written on the first
   *  location fix (WMH 3/4). */
  originLat: number;
  originLng: number;
  /** Encoded Google overview polyline for the road route origin→destination,
   *  fetched ONCE at start. Absent → tracker falls back to two pins + a straight
   *  line. (WMH 3/4) */
  routePolyline?: string;
  routeDistanceMeters?: number | null;
  routeDurationSeconds?: number | null;
  etaMinutes: number;
  startedAt: number;
  /** startedAt + etaMinutes*60000 — moment the scheduled function compares
   *  against to decide if a delay alert is due. Stored once at start so the
   *  function does not have to recompute from etaMinutes (which simplifies
   *  the rule set in production). */
  expectedArrivalMs: number;
  status: WalkSessionStatus;
  /** Current location, updated every {LOCATION_CADENCE_MS}ms by the walker. */
  lat?: number;
  lng?: number;
  updatedAt?: number;
  arrivedAt?: number;
  cancelledAt?: number;
  /** Set by the Firebase scheduled function the first time it sends a delay
   *  alert SMS — keeps the function idempotent across the 1-minute schedule. */
  delayAlertSentAt?: number;
  watchers: Record<string, WalkSessionWatcher>;
}

/** RTDB push cadence for the walker's location (Batch D decision #4). */
export const LOCATION_CADENCE_MS = 12_000;

/** Distance threshold for auto-arrival (P0-17a — 50 metres). */
export const ARRIVAL_RADIUS_METERS = 50;

/** Grace period before the scheduled function sends a delay SMS (P0-17b). */
export const DELAY_ALERT_GRACE_MS = 5 * 60_000;

/** Stop sending delay alerts after this many ms past ETA — once is enough. */
export const DELAY_ALERT_WINDOW_MS = 60 * 60_000;

/**
 * Haversine distance in metres between two lat/lng points. Sufficient for
 * the < 100m geofence check we use here — accuracy errors at this scale are
 * dominated by GPS noise, not the spherical-earth assumption.
 */
export function haversineMeters(
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number,
): number {
  const R = 6_371_000; // earth radius, metres
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const x =
    sinDLat * sinDLat +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * sinDLng * sinDLng;
  const c = 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  return R * c;
}

/**
 * Returns true when the walker has crossed inside the destination geofence.
 * Used by the WalkMeHomeContext to auto-mark arrival without forcing a
 * "did you make it?" prompt. Returns false on missing inputs so callers
 * can chain it without null-checking.
 */
export function isWithinArrival(
  currentLat: number | null | undefined,
  currentLng: number | null | undefined,
  destLat: number,
  destLng: number,
  radiusMeters: number = ARRIVAL_RADIUS_METERS,
): boolean {
  if (
    typeof currentLat !== "number" ||
    typeof currentLng !== "number" ||
    !isFinite(currentLat) ||
    !isFinite(currentLng) ||
    (currentLat === 0 && currentLng === 0)
  ) {
    return false;
  }
  return (
    haversineMeters(currentLat, currentLng, destLat, destLng) <= radiusMeters
  );
}

/**
 * Recompute a dynamic ETA given current location and average pace. We
 * intentionally use a simple straight-line distance / pace rather than
 * re-querying Google Directions on every push: the Directions cost would
 * dominate, and the dynamic ETA is for UX comfort (not safety-critical
 * timing — the scheduled delay alert keys off the *initial* expectedArrival,
 * not the running estimate).
 */
export function recomputeEtaMinutes(
  currentLat: number,
  currentLng: number,
  destLat: number,
  destLng: number,
  paceMps: number = 1.4 /* avg walking pace, ~3.1 mph */,
): number {
  if (paceMps <= 0) return 0;
  const meters = haversineMeters(currentLat, currentLng, destLat, destLng);
  const minutes = meters / paceMps / 60;
  if (!isFinite(minutes)) return 0;
  return Math.max(1, Math.ceil(minutes));
}

/**
 * Pure decision: should the scheduled function send a delay alert for this
 * session right now? Returns the reason as a discriminated union so the
 * caller can both decide what to do and log the reason. The scheduled
 * function calls this for every active session each minute.
 */
export type DelayAlertDecision =
  | { send: false; reason: "not_active" }
  | { send: false; reason: "already_alerted" }
  | { send: false; reason: "not_yet_due" }
  | { send: false; reason: "outside_window" }
  | { send: false; reason: "no_watchers" }
  | { send: true; reason: "due"; minutesPastEta: number };

export function decideDelayAlert(
  session: Pick<
    WalkSessionShape,
    "status" | "expectedArrivalMs" | "delayAlertSentAt" | "watchers"
  >,
  now: number,
  graceMs: number = DELAY_ALERT_GRACE_MS,
  windowMs: number = DELAY_ALERT_WINDOW_MS,
): DelayAlertDecision {
  if (session.status !== "active") {
    return { send: false, reason: "not_active" };
  }
  if (session.delayAlertSentAt && session.delayAlertSentAt > 0) {
    return { send: false, reason: "already_alerted" };
  }
  const elapsedPastEta = now - session.expectedArrivalMs;
  if (elapsedPastEta < graceMs) {
    return { send: false, reason: "not_yet_due" };
  }
  if (elapsedPastEta > windowMs) {
    return { send: false, reason: "outside_window" };
  }
  const watcherCount = Object.keys(session.watchers ?? {}).length;
  if (watcherCount === 0) {
    return { send: false, reason: "no_watchers" };
  }
  return {
    send: true,
    reason: "due",
    minutesPastEta: Math.floor(elapsedPastEta / 60_000),
  };
}

/**
 * Compose the SMS body sent to watchers when a walk starts. Short, friendly,
 * action-oriented. Stays under 160 chars so it ships as one SMS segment.
 */
export function buildStartWalkSms(input: {
  walkerName: string;
  destinationName: string;
  etaMinutes: number;
  trackLink: string;
  /** "walk" (default) or "drive" (car / rideshare). Governs the verb so the
   *  SMS reads correctly for both — e.g. driving home or in an Uber. */
  mode?: TravelMode;
}): string {
  const name = (input.walkerName || "Someone").trim();
  const dest = (input.destinationName || "their destination").trim();
  const eta = Math.max(1, Math.floor(input.etaMinutes));
  const label =
    normalizeTravelMode(input.mode) === "drive"
      ? "Drive Me Home"
      : "Walk Me Home";
  return `MyAmbits: ${name} started a ${label} to ${dest} (ETA ${eta} min). Track: ${input.trackLink}`;
}

/**
 * Compose the SMS body sent to watchers when the walker marks safe.
 */
export function buildMarkSafeSms(input: {
  walkerName: string;
  destinationName: string;
}): string {
  const name = (input.walkerName || "They").trim();
  const dest = (input.destinationName || "their destination").trim();
  return `MyAmbits: ${name} made it to ${dest} safely.`;
}

/**
 * Compose the SMS body sent by the scheduled function when the walker is
 * delayed past ETA.
 */
export function buildDelayAlertSms(input: {
  walkerName: string;
  destinationName: string;
  minutesPastEta: number;
  trackLink: string;
}): string {
  const name = (input.walkerName || "Someone").trim();
  const dest = (input.destinationName || "their destination").trim();
  const mins = Math.max(1, Math.floor(input.minutesPastEta));
  return `MyAmbits: ${name} is ${mins} min past ETA on a Walk Me Home to ${dest}. Check on them: ${input.trackLink}`;
}
